import { GENDER, IGender } from '@src/constants';
import {
  QUIZZES_ALIGN,
  QUIZZES_ANSWERS_TYPES,
  QUIZZES_CONTENT_TYPES,
  QUIZZES_CONTENT_TYPES_LIST,
} from '@src/constants/quizzes.constants';
import {
  IQuizzesAnswer,
  IQuizzesContentData,
  IQuizzesContentItem,
  IQuizzesContentTypes,
  IQuizzesGender,
  IQuizzesSocialContentType,
} from '@src/interfaces/quizzes.interfaces';

export const getQuizzesAnswerKey = (type: IQuizzesContentTypes, step: number) =>
  [type, step].join('_');

export const getQuizzesGender = (
  gender: IGender | null,
  defaultGender?: IGender,
): IQuizzesGender => {
  const value = gender ?? defaultGender;
  return value === GENDER.MALE ? GENDER.MALE : GENDER.FEMALE;
};

export const getQuizzesAnswers = (
  content: IQuizzesContentData,
  answers: Record<string, IQuizzesAnswer> = {},
  gender: IQuizzesGender,
) =>
  content.reduce<Record<string, string | string[]>>((acc, item, step) => {
    if (!QUIZZES_ANSWERS_TYPES.includes(item.type)) return acc;

    const answer = answers[getQuizzesAnswerKey(item.type, step)];
    const itemData = item.data?.[gender];

    if (answer === undefined || !itemData || !('title' in itemData)) return acc;

    const items =
      ('items' in itemData && itemData.items) ||
      ('zones' in itemData && itemData.zones) ||
      [];
    const getText = (index: number) => items[index]?.text;
    const { title } = itemData;

    acc[title] = Array.isArray(answer)
      ? answer.map(getText).filter(Boolean)
      : getText(answer);

    return acc;
  }, {});

export const getQuizzesItemByType = <T extends IQuizzesContentItem['type']>(
  content: IQuizzesContentData | undefined,
  type: T,
) =>
  content?.find(
    (item): item is Extract<IQuizzesContentItem, { type: T }> =>
      item.type === type,
  );

export const getSocialQuizzesContentData = (
  type: IQuizzesSocialContentType,
  isProgressBarVisible: boolean = false,
): IQuizzesContentItem<IQuizzesSocialContentType> => ({
  type,
  data: {
    [GENDER.FEMALE]: { is_progress_bar_visible: isProgressBarVisible },
    [GENDER.MALE]: { is_progress_bar_visible: isProgressBarVisible },
  },
});

export const getQuizzesContent = (
  content: IQuizzesContentData,
): IQuizzesContentData => [
  ...content.flatMap((item): IQuizzesContentData => {
    if (!QUIZZES_CONTENT_TYPES_LIST.includes(item.type)) return [];

    return item.type === QUIZZES_CONTENT_TYPES.OCCASION
      ? [
          item,
          getSocialQuizzesContentData(QUIZZES_CONTENT_TYPES.EVENT_DATE, true),
        ]
      : [item];
  }),
  getSocialQuizzesContentData(QUIZZES_CONTENT_TYPES.GRAPH),
  getSocialQuizzesContentData(QUIZZES_CONTENT_TYPES.PLAN),
  getSocialQuizzesContentData(QUIZZES_CONTENT_TYPES.YOUR_WEIGHT_GRAPH),
  getSocialQuizzesContentData(QUIZZES_CONTENT_TYPES.EMAIL, true),
];

export const getQuizzesItemImage = (
  picture?: string | null,
  align?: string,
) => {
  const image = picture ?? undefined;
  return align === QUIZZES_ALIGN.LEFT ? { leftImage: image } : { image };
};

export const toggleQuizzesAnswer = (answer: number[], value: number) =>
  answer.includes(value)
    ? answer.filter((item) => item !== value)
    : [...answer, value];
